import React, { useCallback, useEffect, useState } from "react";
import { BsFillPersonFill } from "react-icons/bs";
import secureLocalStorage from "react-secure-storage";
import {
  GoogleReCaptcha,
  GoogleReCaptchaProvider,
} from "react-google-recaptcha-v3";

import {
  AuthLogin,
  GetUsersInfo,
  PostNewPassword,
  ResetPassword,
  SendCode,
} from "../../data/services";
import { captchaKeys } from "../../shared/enums/captchaKeys";
import SenhaComponent from "./Senha";
import NewSenhaComponent from "./NewSenha";

import {
  Page,
  LeftPanel,
  BrandRow,
  Headline,
  RightPanel,
  Card,
  FooterLogo,
  Title,
  Tabs,
  TabBtn,
  Field,
  Hint,
  Row,
  Btn,
  LinkLike,
  Alert,
  Spinner,
  CaptchaStyle,
  LeftInner,
  TextSurface,
  LoginForm,
} from "./Login.styles";
import {
  normalizeMatricula,
  safeSwitch,
} from "./services/helpers";
import { canSubmitColab, canSubmitForn } from "./services/guards";

type Tab = "COLAB" | "FORN";

const Login: React.FC = () => {
  const [tab, setTab] = useState<Tab>("COLAB");

  // fluxos
  const [firstAccess, setFirstAccess] = useState(false);
  const [consultEmail, setConsultEmail] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);
  const [modalAlterarSenha, setModalAlterarSenha] = useState(false);

  // formulário
  const [username, setUsername] = useState(""); // matricula (colab) ou email (forn)
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [senhaValida, setSenhaValida] = useState(false);
  const [newPassword, setNewPassword] = useState({
    password: "",
    confirmPassword: "",
  });

  const [res, setRes] = useState<any>();
  const [error, setError] = useState<
    { error: boolean; message: string } | false
  >(false);
  const [errorCode, setErrorCode] = useState({ error: false, text: "" });
  const [user, setUser] = useState<any>("");
  const [captcha, setCaptcha] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const submitLogin = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const ok =
        tab === "COLAB"
          ? canSubmitColab(username, password)
          : canSubmitForn(username, password);

      if (!ok || isSubmitting) return;

      setIsSubmitting(true);

      const type = tab === "COLAB" ? "GP" : "FORN";
      const profile =
        tab === "COLAB" ? "usuarioColaborador" : "usuarioFornecedor";

      AuthLogin({ username, password, profile, setVal: setRes });
      GetUsersInfo(setUser, username, type);
      secureLocalStorage.setItem("profile", profile);
    },
    [tab, username, password, isSubmitting]
  );

  useEffect(() => {
    if (
      res ||
      error ||
      firstAccess ||
      modalAlterarSenha ||
      consultEmail ||
      resetPassword
    ) {
      setIsSubmitting(false);
    }
  }, [res, error, firstAccess, modalAlterarSenha, consultEmail, resetPassword]);

  useEffect(() => {
    if (
      res?.response?.data?.idUsuario &&
      res?.response?.data?.mensagem === "Acesso negado, precisa mudar a senha"
    ) {
      setFirstAccess(true);
    }

    if (res?.response?.data?.mensagem) {
      const msg = res.response.data.mensagem;
      if (
        msg === "Email não existe no cadastro Notifier" ||
        msg === "Erro ao logar usuário interno" ||
        msg === "Matrícula não existe no cadastro Notifier" ||
        res?.response?.status === 401
      ) {
        setError({ error: true, message: msg });
      }
      if (res?.status === 502) {
        secureLocalStorage.clear();
        setError({
          error: true,
          message:
            "Ocorreu um problema ao realizar o login. Atualize a página e tente novamente!",
        });
      }
    }

    if (!!user) {
      const u = user[0];
      if (u) {
        secureLocalStorage.setItem("login", u.login);
        secureLocalStorage.setItem("nomeUsuario", u.nomeUsuario);
        secureLocalStorage.setItem("profile", u.perfilAcesso);
        secureLocalStorage.setItem("idFabrica", u.idFabrica);
        secureLocalStorage.setItem("idUsuario", u.idUsuario);
        secureLocalStorage.setItem("tipo", u.tipo);
        secureLocalStorage.setItem("email", u.email);
        secureLocalStorage.setItem("unidade", u.unidade);
      }
    }

    if (res?.data?.access_token) {
      secureLocalStorage.setItem("access_token", res.data.access_token);
      secureLocalStorage.setItem("username", username);
      window.location.href = "/home";
    }
  }, [res, user, username]);

  // primeiro acesso
  const confirmFirstAccessCode = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!code)
        return setErrorCode({
          error: true,
          text: "Por favor, insira o código",
        });
      setFirstAccess(false);
      setModalAlterarSenha(true);
    },
    [code]
  );

  // reset senha (fornecedor)
  const requestResetCode = async () => {
    try {
      if (!username) return;
      await SendCode({ email: username }, setError);
      setConsultEmail(false);
      setResetPassword(true);
    } catch (err) {
      setErrorCode({ error: true, text: `${err}` });
    }
  };

  const doResetPassword = async () => {
    try {
      if (!senhaValida) return alert("Se atente aos requisitos da senha");
      await ResetPassword(
        { email: username, verificationCode: code, novaSenha: password },
        setError
      );
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setErrorCode({ error: true, text: `${err}` });
    }
  };

  // alterar senha (primeiro acesso)
  const confirmNewPassword = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (newPassword.password !== newPassword.confirmPassword) {
        return setErrorCode({
          error: true,
          text: "As senhas não são iguais. Por favor, tente novamente.",
        });
      }
      try {
        const response = await PostNewPassword({
          idUsuario: res?.response?.data?.idUsuario,
          confirmationCode: code,
          senhaAntiga: password,
          novaSenha: newPassword.password,
        });
        if (response?.status !== 200) {
          return setErrorCode({
            error: true,
            text:
              response?.data?.mensagem || "Ocorreu um erro. Tente novamente.",
          });
        }
        setErrorCode({
          error: false,
          text: "Nova senha confirmada com sucesso!",
        });
        setTimeout(() => (window.location.href = "/"), 1500);
      } catch (err: any) {
        const msg = err?.response?.data?.mensagem;
        setErrorCode({ error: true, text: msg || "Erro ao alterar senha." });
      }
    },
    [code, newPassword, password, res?.response?.data?.idUsuario]
  );

  /** ===== Forms ===== */
  const ColaboradorForm = (
    <LoginForm onSubmit={submitLogin}>
      <Hint>
        Se for <b>Terceiro</b>, use <b>C</b> + 8 dígitos (ex.: <b>C12345678</b>
        ).
        <br /> Caso contrário, use apenas 8 dígitos.
      </Hint>

      <Field
        placeholder="Matrícula"
        value={username}
        onChange={(e) => {
          setError(false);
          setUsername(normalizeMatricula(e.target.value));
        }}
        hasError={!!error}
        autoComplete="username"
        aria-label="Matrícula do colaborador"
      />

      <Field
        type="password"
        placeholder="Senha"
        value={password}
        onChange={(e) => {
          setError(false);
          setPassword(e.target.value);
        }}
        autoComplete="current-password"
        aria-label="Senha"
      />

      <Row>
        <Btn
          type="submit"
          disabled={!canSubmitColab(username, password) || isSubmitting}
        >
          {isSubmitting ? <Spinner aria-label="carregando" /> : "Entrar"}
        </Btn>
      </Row>

      {error && error.error && <Alert tone="danger">{error.message}</Alert>}
    </LoginForm>
  );

  const FornecedorForm = (
    <LoginForm onSubmit={submitLogin}>
      <Field
        type="email"
        placeholder="E-mail do fornecedor"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        hasError={!!error}
        autoComplete="email"
        aria-label="E-mail"
      />
      <Field
        type="password"
        placeholder="Senha"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        aria-label="Senha"
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "6px 0 12px",
        }}
      >
        <LinkLike onClick={safeSwitch(() => setConsultEmail(true))}>
          Esqueci minha senha
        </LinkLike>
      </div>

      <Row>
        <Btn
          type="submit"
          disabled={!canSubmitForn(username, password) || isSubmitting}
        >
          {isSubmitting ? <Spinner aria-label="carregando" /> : "Entrar"}
        </Btn>
      </Row>

      {error && error.error && <Alert tone="danger">{error.message}</Alert>}

      <div className="grecaptcha-badge">
        <GoogleReCaptcha onVerify={(val) => setCaptcha(!!val)} />
      </div>
    </LoginForm>
  );

  const PrimeiroAcessoForm = (
    <form onSubmit={confirmFirstAccessCode}>
      <Title>Primeiro acesso</Title>
      <p
        style={{
          color: "#6b7280",
          fontSize: 14,
          lineHeight: 1.55,
          marginTop: -6,
        }}
      >
        Enviamos um código para o seu e-mail. Digite abaixo para confirmar e
        criar a nova senha.
      </p>
      <Field
        placeholder="Código recebido por e-mail"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <Row>
        <Btn type="submit">Confirmar</Btn>
      </Row>
      {errorCode.error && <Alert tone="danger">{errorCode.text}</Alert>}
      {!errorCode.error && errorCode.text && (
        <Alert tone="success">{errorCode.text}</Alert>
      )}
    </form>
  );

  const ResetEmailForm = (
    <form onSubmit={(e) => e.preventDefault()}>
      <Title>Redefinir senha</Title>
      <p
        style={{
          color: "#6b7280",
          fontSize: 14,
          lineHeight: 1.55,
          marginTop: -6,
        }}
      >
        Informe seu e-mail para enviarmos um código de verificação.
      </p>
      <Field
        type="email"
        placeholder="Seu e-mail"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <Row>
        <Btn variant="light" onClick={safeSwitch(() => setConsultEmail(false))}>
          Voltar
        </Btn>
        <Btn onClick={safeSwitch(requestResetCode)}>Validar e-mail</Btn>
      </Row>
      {error && (error as any).error && (
        <Alert tone="danger">{(error as any).message}</Alert>
      )}
    </form>
  );

  const ResetPasswordForm = (
    <form onSubmit={(e) => e.preventDefault()}>
      <Title>Redefinir senha</Title>
      <Field
        placeholder="Código recebido"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <SenhaComponent validaSenha={setSenhaValida} senhaAtual={setPassword} />
      <Row>
        <Btn
          variant="light"
          onClick={safeSwitch(() => {
            setResetPassword(false);
            setConsultEmail(true);
          })}
        >
          Voltar
        </Btn>
        <Btn onClick={safeSwitch(doResetPassword)} disabled={!senhaValida}>
          Salvar nova senha
        </Btn>
      </Row>
      {errorCode.error && <Alert tone="danger">{errorCode.text}</Alert>}
      {!errorCode.error && errorCode.text && (
        <Alert tone="success">{errorCode.text}</Alert>
      )}
    </form>
  );

  const AlterarSenhaPrimeiroAcessoForm = (
    <form onSubmit={confirmNewPassword}>
      <Title>Definir nova senha</Title>
      <NewSenhaComponent
        validaSenha={setSenhaValida}
        senhaAtual={setNewPassword}
      />
      <Row>
        <Btn type="submit" disabled={!senhaValida}>
          Confirmar
        </Btn>
      </Row>
      {errorCode.error && <Alert tone="danger">{errorCode.text}</Alert>}
      {!errorCode.error && errorCode.text && (
        <Alert tone="success">{errorCode.text}</Alert>
      )}
    </form>
  );

  const renderCurrentForm = () => {
    if (firstAccess) return PrimeiroAcessoForm;
    if (modalAlterarSenha) return AlterarSenhaPrimeiroAcessoForm;
    if (consultEmail) return ResetEmailForm;
    if (resetPassword) return ResetPasswordForm;
    return tab === "COLAB" ? ColaboradorForm : FornecedorForm;
  };

  return (
    <GoogleReCaptchaProvider
      language="pt-BR"
      reCaptchaKey={captchaKeys.USERS_CAPTCHA_KEY}
    >
      <CaptchaStyle />
      <Page>
        <LeftPanel bg="/assets/img/ita2.jpeg">
          <LeftInner>
            <TextSurface>
              <BrandRow>
                <div className="brand-name">Notifier · Grupo Petrópolis</div>
              </BrandRow>

              <Headline>Portal de Não Conformidades de Embalagens</Headline>
            </TextSurface>
          </LeftInner>
        </LeftPanel>

        <RightPanel>
          <Card>
            <Title>Login</Title>

            {/* Tabs só na tela principal */}
            {!firstAccess &&
              !consultEmail &&
              !resetPassword &&
              !modalAlterarSenha && (
                <Tabs role="tablist" aria-label="Tipo de acesso">
                  <TabBtn
                    active={tab === "COLAB"}
                    onClick={safeSwitch(() => {
                      setTab("COLAB");
                      setError(false);
                    })}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <BsFillPersonFill /> Colaborador
                    </span>
                  </TabBtn>
                  <TabBtn
                    active={tab === "FORN"}
                    onClick={safeSwitch(() => {
                      setTab("FORN");
                      setError(false);
                    })}
                  >
                    @ Fornecedor
                  </TabBtn>
                </Tabs>
              )}

            {renderCurrentForm()}

            <FooterLogo>
              <img
                className="logo-petropolis"
                src="/assets/img/logo-petropolis.png"
                alt="Petrópolis"
              />
            </FooterLogo>
          </Card>
        </RightPanel>
      </Page>
    </GoogleReCaptchaProvider>
  );
};

export default Login;
